import React, { useEffect, useState } from 'react'
import { CaretDown, CaretRight, Minus, Plus, UserCircle, X } from '@phosphor-icons/react'
import type { AutoReplyConfig as AutoReplyConfigType, AutoReplyShared, WorkflowNodeData } from '@/types/workflow'
import type { TemplateListItem } from '@/types/template'
import { workflowsApi, mailTemplatesApi, type AssignableMember } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import {
  inputClass, inputStyle, textareaClass, sectionClass, infoBannerClass,
  checkboxInputClass, checkboxLabelClass, pillBaseClass, pillActiveClass, pillInactiveClass,
  pillTextStyle, cardChromeClass,
} from './_kit'
import MaterialSelector from './MaterialSelector'
import {
  MAX_FOLLOW_UPS, clearOverride, effectiveFollowUp, normalizeAutoReply, setOverride, validateAutoReply,
} from '../../autoReplyConfig'

interface AutoReplyConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'casual', label: 'Casual' },
  { value: 'persuasive', label: 'Persuasive' },
  { value: 'urgent', label: 'Urgent' },
]
const PURPOSES = [
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'pitch', label: 'Sales Pitch' },
  { value: 'nurture', label: 'Nurture' },
  { value: 're_engage', label: 'Re-engage' },
  { value: 'thank_you', label: 'Thank You' },
]
const WRITE_MODES = [
  { value: 'ai', label: 'AI-generated' },
  { value: 'manual', label: 'Manual' },
]
const THREADING = [
  { value: 'same_thread', label: 'Reply in the same thread' },
  { value: 'fresh', label: 'Send as a fresh email' },
]
const WAIT_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'hours', label: 'Hours' },
]
const TASK_TYPES = [
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'custom', label: 'Custom' },
]
const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]
const LEAD_STATUSES = [
  { value: 'none', label: 'No change' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'qualified', label: 'Qualified' },
]
const PERSONALIZATION: { value: string; label: string }[] = [
  { value: 'job_title', label: 'Job title' },
  { value: 'company_name', label: 'Company' },
  { value: 'company_industry', label: 'Industry' },
  { value: 'department', label: 'Department' },
  { value: 'city', label: 'City' },
  { value: 'country', label: 'Country' },
]

const eyebrow = { fontSize: '10px', fontWeight: 590, letterSpacing: '0.09em' } as const

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true)
  return (
    <div className={`border ${cardChromeClass}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
      >
        {open ? <CaretDown className="w-3.5 h-3.5 text-muted-foreground" /> : <CaretRight className="w-3.5 h-3.5 text-muted-foreground" />}
        <span className="text-foreground" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>{title}</span>
      </button>
      {open && <div className="px-3 pb-3 space-y-4">{children}</div>}
    </div>
  )
}

interface ContentFieldsProps {
  scope: 'shared' | number
  value: AutoReplyShared
  onChange: (patch: Partial<AutoReplyShared>) => void
  templates: TemplateListItem[]
  bodyError?: string
}

/** The write-mode, tone, purpose, template, materials and threading controls for one follow-up setup. */
function ContentFields({ scope, value, onChange, templates, bodyError }: ContentFieldsProps) {
  const L = (base: string) => (scope === 'shared' ? base : `${base} for follow-up ${scope}`)
  const manual = value.write_mode === 'manual'
  const toggleField = (f: string) =>
    onChange({
      contact_fields: value.contact_fields.includes(f)
        ? value.contact_fields.filter((x) => x !== f)
        : [...value.contact_fields, f],
    })

  return (
    <div className="space-y-3">
      <Field label={L('Write mode')}>
        {(f) => (
          <Select {...f} value={value.write_mode} onChange={(v) => onChange({ write_mode: v as AutoReplyShared['write_mode'] })} options={WRITE_MODES} />
        )}
      </Field>

      {manual ? (
        <>
          <Field label={L('Subject')}>
            {(f) => (
              <input {...f} type="text" className={inputClass} style={inputStyle} value={value.subject}
                onChange={(e) => onChange({ subject: e.target.value })} placeholder="Following up on my last email" />
            )}
          </Field>
          <Field label={L('Email body')} error={bodyError} hint="Supports {{contact.first_name}} style placeholders.">
            {(f) => (
              <textarea {...f} rows={5} className={textareaClass} style={inputStyle} value={value.body}
                onChange={(e) => onChange({ body: e.target.value })} />
            )}
          </Field>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label={L('Tone')}>
              {(f) => <Select {...f} value={value.tone} onChange={(v) => onChange({ tone: v as AutoReplyShared['tone'] })} options={TONES} />}
            </Field>
            <Field label={L('Purpose')}>
              {(f) => <Select {...f} value={value.purpose} onChange={(v) => onChange({ purpose: v })} options={PURPOSES} />}
            </Field>
          </div>
          {templates.length > 0 && (
            <Field label={L('Structure template')} hint="Defines the block structure the AI follows.">
              {(f) => (
                <Select
                  {...f}
                  value={value.template_id != null ? String(value.template_id) : 'default'}
                  onChange={(v) => onChange({ template_id: v === 'default' ? null : Number(v) })}
                  options={[
                    { value: 'default', label: 'Default template' },
                    ...templates.map((t) => ({ value: String(t.id), label: t.name })),
                  ]}
                />
              )}
            </Field>
          )}
          <Field label={L('Personalize with')} as="group">
            <div className="flex flex-wrap gap-1.5">
              {PERSONALIZATION.map((p) => {
                const on = value.contact_fields.includes(p.value)
                return (
                  <button
                    key={p.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleField(p.value)}
                    className={`${pillBaseClass} ${on ? pillActiveClass : pillInactiveClass}`}
                    style={pillTextStyle}
                  >
                    {p.label}
                  </button>
                )
              })}
            </div>
          </Field>
          <div>
            <MaterialSelector selectedIds={value.material_ids} onChange={(ids) => onChange({ material_ids: ids })} />
            <p className="mt-1 text-xs text-muted-foreground">Documents the AI may draw on.</p>
          </div>
          <Field label={L('Custom instructions')}>
            {(f) => (
              <textarea {...f} rows={3} className={textareaClass} style={inputStyle} value={value.custom_instructions}
                onChange={(e) => onChange({ custom_instructions: e.target.value })}
                placeholder="Anything the AI should keep in mind" />
            )}
          </Field>
        </>
      )}

      <Field label={L('Threading')}>
        {(f) => <Select {...f} value={value.threading} onChange={(v) => onChange({ threading: v as AutoReplyShared['threading'] })} options={THREADING} />}
      </Field>
    </div>
  )
}

export default function AutoReplyConfig({ data, updateField }: AutoReplyConfigProps) {
  const cfg = normalizeAutoReply(data.auto_reply_config)
  const errors = validateAutoReply(cfg)
  const currentUser = useAuthStore((s) => s.user)

  const [members, setMembers] = useState<AssignableMember[]>([])
  const [dropdownEligible, setDropdownEligible] = useState(false)
  const [loadingMembers, setLoadingMembers] = useState(true)
  const [pickerValue, setPickerValue] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [templates, setTemplates] = useState<TemplateListItem[]>([])
  const [openStep, setOpenStep] = useState<number | null>(null)
  const [lastFocused, setLastFocused] = useState<'title' | 'description'>('title')

  useEffect(() => {
    let cancelled = false
    workflowsApi
      .assignableMembers()
      .then((res) => {
        if (cancelled) return
        setMembers(res.data.members)
        setDropdownEligible(res.data.dropdown_eligible)
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingMembers(false) })
    mailTemplatesApi.getAll().then((res) => { if (!cancelled) setTemplates(res.data) }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  const write = (next: AutoReplyConfigType) => updateField('auto_reply_config', normalizeAutoReply(next))
  const patchNoReply = (p: Partial<AutoReplyConfigType['no_reply']>) => write({ ...cfg, no_reply: { ...cfg.no_reply, ...p } })
  const patchShared = (p: Partial<AutoReplyShared>) => patchNoReply({ shared: { ...cfg.no_reply.shared, ...p } })
  const patchReply = (p: Partial<AutoReplyConfigType['reply']>) => write({ ...cfg, reply: { ...cfg.reply, ...p } })
  const patchTask = (p: Partial<AutoReplyConfigType['reply']['task']>) => patchReply({ task: { ...cfg.reply.task, ...p } })

  const numberChange = (apply: (n: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') return // let the user clear the box while typing
    apply(Number(e.target.value))
  }

  const pool = cfg.reply.team_member_emails
  const addEmail = (email: string) => {
    const trimmed = email.trim()
    if (!trimmed || pool.includes(trimmed)) return
    patchReply({ team_member_emails: [...pool, trimmed] })
  }
  const removeEmail = (email: string) => patchReply({ team_member_emails: pool.filter((e) => e !== email) })
  const memberByEmail = new Map(members.map((m) => [m.email, m]))
  const labelFor = (email: string) => memberByEmail.get(email)?.full_name || email
  const availableMembers = members.filter((m) => !pool.includes(m.email))

  const insertToken = (token: string) => {
    if (lastFocused === 'description') {
      patchTask({ description_template: `${cfg.reply.task.description_template}${token}` })
    } else {
      patchTask({ title_template: `${cfg.reply.task.title_template}${token}` })
    }
  }

  const poolError = errors.find((e) => /team member/i.test(e))
  const titleError = errors.find((e) => /title/i.test(e))
  const sharedBodyError = errors.find((e) => /^Follow-ups are in manual mode/i.test(e))
  const stepBodyError = (step: number) => errors.find((e) => e.startsWith(`Follow-up ${step} is in manual mode`))

  const unit = cfg.no_reply.wait_unit

  return (
    <div className="space-y-3">
      <Field label="Label">
        {(f) => (
          <input {...f} type="text" className={inputClass} style={inputStyle} value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)} placeholder="Auto Reply" />
        )}
      </Field>

      <Section title="If no reply">
        <div className={sectionClass}>
          <Field label="Follow-ups" as="group" hint={`Sent one after another until the contact replies. Up to ${MAX_FOLLOW_UPS}.`}>
            <div className="inline-flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" aria-label="Remove a follow-up"
                disabled={cfg.no_reply.follow_up_count <= 1}
                onClick={() => patchNoReply({ follow_up_count: cfg.no_reply.follow_up_count - 1 })}
                icon={<Minus className="w-3.5 h-3.5" />} />
              <span aria-label="Number of follow-ups" className="min-w-6 text-center text-foreground" style={{ fontSize: '14px', fontWeight: 590 }}>
                {cfg.no_reply.follow_up_count}
              </span>
              <Button type="button" variant="outline" size="sm" aria-label="Add a follow-up"
                disabled={cfg.no_reply.follow_up_count >= MAX_FOLLOW_UPS}
                onClick={() => patchNoReply({ follow_up_count: cfg.no_reply.follow_up_count + 1 })}
                icon={<Plus className="w-3.5 h-3.5" />} />
            </div>
          </Field>

          <Field label="Wait between follow-ups">
            {(f) => (
              <div className="flex gap-2">
                <input {...f} type="number" min={1} className={`${inputClass} flex-1`} style={inputStyle}
                  value={cfg.no_reply.wait_value} onChange={numberChange((n) => patchNoReply({ wait_value: n }))} />
                <Select ariaLabel="Unit" value={unit} onChange={(v) => patchNoReply({ wait_unit: v as 'days' | 'hours' })} options={WAIT_UNITS} />
              </div>
            )}
          </Field>

          <label className={checkboxLabelClass}>
            <input type="checkbox" className={checkboxInputClass} checked={cfg.no_reply.business_days_only}
              onChange={(e) => patchNoReply({ business_days_only: e.target.checked })} />
            <span style={{ fontSize: '13px' }}>Business days only</span>
          </label>

          <div className="space-y-3">
            <p className="uppercase text-muted-foreground/80 dark:text-[#62666d]" style={eyebrow}>Shared content for all follow-ups</p>
            <ContentFields scope="shared" value={cfg.no_reply.shared} onChange={patchShared} templates={templates} bodyError={sharedBodyError} />
          </div>

          <div className="space-y-2">
            <p className="uppercase text-muted-foreground/80 dark:text-[#62666d]" style={eyebrow}>Each follow-up</p>
            {Array.from({ length: cfg.no_reply.follow_up_count }, (_, i) => i + 1).map((step) => {
              const customized = Boolean(cfg.no_reply.overrides[String(step)])
              const expanded = openStep === step
              return (
                <div key={step} className={`border ${cardChromeClass} px-3 py-2`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col items-start gap-1 min-w-0">
                      <span className="text-foreground whitespace-nowrap" style={{ fontSize: '13px', fontWeight: 510 }}>Follow-up {step}</span>
                      {customized && (
                        <span className={`${pillBaseClass} ${pillActiveClass}`} style={pillTextStyle}>Customized</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {customized && (
                        <Button type="button" variant="ghost" size="sm" aria-label={`Reset follow-up ${step} to shared`}
                          onClick={() => { write(clearOverride(cfg, step)); setOpenStep(null) }}>
                          Reset
                        </Button>
                      )}
                      <Button type="button" variant="outline" size="sm" aria-label={`Customize follow-up ${step}`}
                        onClick={() => setOpenStep(expanded ? null : step)}>
                        {expanded ? 'Done' : 'Customize'}
                      </Button>
                    </div>
                  </div>
                  {!expanded && !customized && (
                    <p className="mt-1 text-xs text-muted-foreground">Uses the shared content above.</p>
                  )}
                  {expanded && (
                    <div className="mt-3">
                      <ContentFields
                        scope={step}
                        value={effectiveFollowUp(cfg, step)}
                        onChange={(patch) => write(setOverride(cfg, step, patch))}
                        templates={templates}
                        bodyError={stepBodyError(step)}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className={infoBannerClass}>
            <p className="text-xs">After the last follow-up, a contact who still has not replied leaves the sequence quietly. No tag and no task.</p>
          </div>
        </div>
      </Section>

      <Section title="If they reply">
        <div className={sectionClass}>
          <label className={checkboxLabelClass}>
            <input type="checkbox" className={checkboxInputClass} checked disabled readOnly aria-label="Stop remaining follow-ups" />
            <span style={{ fontSize: '13px' }}>Stop remaining follow-ups</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-2">
            Any reply counts. Once a person is in the conversation, automated follow-ups stop right away.
          </p>

          <Field label="Task type">
            {(f) => <Select {...f} value={cfg.reply.task.type} onChange={(v) => patchTask({ type: v as 'follow_up' | 'custom' })} options={TASK_TYPES} />}
          </Field>

          <Field label="Task title" error={titleError}>
            {(f) => (
              <input {...f} type="text" className={inputClass} style={inputStyle} value={cfg.reply.task.title_template}
                onFocus={() => setLastFocused('title')}
                onChange={(e) => patchTask({ title_template: e.target.value })} />
            )}
          </Field>

          <Field label="Task description" hint="Optional.">
            {(f) => (
              <textarea {...f} rows={3} className={textareaClass} style={inputStyle} value={cfg.reply.task.description_template}
                onFocus={() => setLastFocused('description')}
                onChange={(e) => patchTask({ description_template: e.target.value })} />
            )}
          </Field>

          <div className="flex flex-wrap gap-1.5">
            {['{contact}', '{reply_snippet}'].map((token) => (
              <button key={token} type="button" aria-label={`Insert ${token}`} onClick={() => insertToken(token)}
                className={`${pillBaseClass} ${pillInactiveClass}`} style={pillTextStyle}>
                {token}
              </button>
            ))}
          </div>

          <label className={checkboxLabelClass}>
            <input type="checkbox" className={checkboxInputClass} checked={cfg.reply.task.include_reply_snippet}
              onChange={(e) => patchTask({ include_reply_snippet: e.target.checked })} />
            <span style={{ fontSize: '13px' }}>Include the reply text in the task</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority">
              {(f) => <Select {...f} value={cfg.reply.task.priority} onChange={(v) => patchTask({ priority: v as 'low' | 'medium' | 'high' })} options={PRIORITIES} />}
            </Field>
            <Field label="Due in (days)">
              {(f) => (
                <input {...f} type="number" min={1} max={30} className={inputClass} style={inputStyle}
                  value={cfg.reply.task.due_in_days} onChange={numberChange((n) => patchTask({ due_in_days: n }))} />
              )}
            </Field>
          </div>
        </div>
      </Section>

      <Section title="Assign and tag">
        <div className={sectionClass}>
          <p className="text-xs text-muted-foreground">
            Tasks rotate evenly across the team below. Teammates who are no longer active are skipped.
          </p>

          {loadingMembers ? null : dropdownEligible ? (
            <Field label="Team members" error={poolError}>
              {(f) => (
                <Select
                  {...f}
                  value={pickerValue}
                  onChange={(email) => { addEmail(email); setPickerValue('') }}
                  options={availableMembers.map((m) => ({ value: m.email, label: m.full_name, description: m.email }))}
                  placeholder={availableMembers.length ? 'Choose a teammate…' : 'Everyone is already added'}
                  disabled={availableMembers.length === 0}
                  buttonClassName="w-full"
                />
              )}
            </Field>
          ) : (
            <Field label="Team member emails" error={poolError}>
              {(f) => (
                <>
                  <div className="flex gap-2">
                    <input {...f} type="text" className={inputClass} style={inputStyle} value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(newEmail); setNewEmail('') } }}
                      placeholder="team@example.com" />
                    <button type="button" onClick={() => { addEmail(newEmail); setNewEmail('') }}
                      className="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90 transition-all duration-150 flex-shrink-0">
                      Add
                    </button>
                  </div>
                  {currentUser?.email && !pool.includes(currentUser.email) && (
                    <Button type="button" variant="outline" size="sm" className="mt-2"
                      onClick={() => addEmail(currentUser.email)} icon={<UserCircle className="w-3.5 h-3.5" />}>
                      Assign to me
                    </Button>
                  )}
                </>
              )}
            </Field>
          )}

          {pool.length > 0 && (
            <div className="space-y-1">
              {pool.map((email) => (
                <div key={email} className="flex items-center justify-between px-3 py-1.5 bg-muted rounded-lg">
                  <span className="text-sm text-foreground truncate">{labelFor(email)}</span>
                  <button type="button" onClick={() => removeEmail(email)} aria-label={`Remove ${labelFor(email)}`}
                    className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-500/15 rounded transition-all duration-150 flex-shrink-0 ml-2">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Set lead status">
              {(f) => (
                <Select {...f} value={cfg.reply.set_lead_status ?? 'none'}
                  onChange={(v) => patchReply({ set_lead_status: v === 'none' ? null : (v as 'contacted' | 'engaged' | 'qualified') })}
                  options={LEAD_STATUSES} />
              )}
            </Field>
            <Field label="Tag">
              {(f) => (
                <input {...f} type="text" className={inputClass} style={inputStyle} value={cfg.reply.add_tag ?? ''}
                  onChange={(e) => patchReply({ add_tag: e.target.value || null })} placeholder="Replied" />
              )}
            </Field>
          </div>

          <div className={infoBannerClass}>
            <p className="text-xs">
              Reply detection needs inbox monitoring (IMAP) enabled on the sending mailbox. Without it, replies are not noticed and follow-ups keep going.
            </p>
          </div>
        </div>
      </Section>
    </div>
  )
}
